<template>
    <div class="search-container">
        <div class="search-inner container mx-auto py-8">
            <form class="md:form-inline" @submit="submit" ref="form">
                <div class="form-group md:w-2/5">
                    <label for="search-location">
                        Location

                        <a v-if="!clinic" ref="currentLocation" href="#" class="text-sm ml-2" @click.prevent="initBrowserGeolocation">
                            <span class="md:hidden lg:inline">Use my current location</span>
                            <span class="hidden md:inline lg:hidden">Use current</span>
                        </a>

                        <a v-else href="#" class="text-sm ml-2" @click.prevent="clearSelectedClinic">
                            Change location
                        </a>
                    </label>

                    <input v-show="!clinic"
                        id="search-location"
                        ref="location"
                        name="location"
                        autocomplete="off"
                        v-model="locationInput"
                        class="form-control no-min-w"
                        placeholder="Enter your location..."
                        required
                    />

                    <div v-if="clinic" class="relative">
                        <input id="clinic"
                            ref="locationClinic"
                            class="form-control no-min-w"
                            readonly
                            :value="clinic.ClinicName"
                        />

                        <a href="#"
                        class="py-1 px-4 text-3xl absolute top-0 right-0 leading-none"
                        @click.prevent="clearSelectedClinic"
                        >&times;</a>
                    </div>

                    <div v-if="locationError" class="form-error">{{ locationError }}</div>
                </div>

                <div class="form-group md:w-1/5">
                    <label for="search-date">Date</label>

                    <Datepicker
                            @selected="setSelectedDate"
                            id="search-date"
                            name="date"
                            :value="dateInput"
                            input-class="form-control no-min-w dropdown"
                            placeholder="ASAP"
                            maximum-view="day"
                            :disabled-dates="disabledDates"
                            :monday-first="true"
                            :highlighted="highlightedDates"
                    />
                </div>

                <div class="form-group md:w-1/5">
                    <label for="search-sort" @click="$refs.sort.focus()">Sort</label>
                    <custom-select ref="sort" v-model="sortInput" id="search-sort" :options="sortOptions" :attributes="{class: 'no-min-w'}"/>
                </div>

                <div class="form-group form-actions md:w-1/5">
                    <button type="submit"
                            role="button"
                            class="btn btn-primary"
                            :class="{loading: isSubmitting || loadingAppointments}"
                    >
                        Search
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
    import { mapActions, mapState } from 'vuex';

    import CustomSelect from './Element/CustomSelect';
    import Datepicker from 'vuejs-datepicker';
    import FormCriteria from '../../lib/FormCriteria';
    import Place from '../../lib/Place';

    export default {
        name: 'search-form',

        components: {
            CustomSelect,
            Datepicker,
        },

        data() {
            const minDate = window.moment().startOf('day').toDate();
            const maxDate = window.moment().add(parseInt(process.env.VUE_APP_MAX_DAYS) + 1, 'day').startOf('day').toDate();

            let queryString = {};
            let autoSubmit = false;
            let clinicLimit = null;

            if (location.search) {
                queryString = window.queryString.parse(location.search);

                if (queryString.submit) {
                    autoSubmit = true;
                }
            }

          if (queryString.clinic_limit) {
            const parsed = parseInt(queryString.clinic_limit);
            if (!isNaN(parsed)) {
              clinicLimit = parsed;
            }
          }

            return {
                // The parsed query string.
                queryString: queryString,

                // The instance of the google.maps.places.Autocomplete
                autocomplete: null,

                disabledDates: {
                    to:   minDate, // Disable all dates up to specific date
                    from: maxDate, // Disable all dates after specific date
                    days: [0],     // Disable Sunday's
                },

                highlightedDates: {
                    dates: [minDate],
                },

                // The list of available sorting options
                sortOptions: [{
                    label: 'Soonest',
                    value: 'soonest',
                }, {
                    label: 'Closest',
                    value: 'closest',
                }],

                // A flag to indicate if the form is currently submitting
                isSubmitting: false,

                // A flag to indicate if the form has already been submitted
                hasBeenSubmitted: false,

                // A flag to indicate if we should auto-submit the form.
                autoSubmit,

                clinicLimit,

                // Form v-models
                locationInput: '',
                locationError: null,
                dateInput: '',
                sortInput: 'soonest',
            };
        },

        computed: {
            ...mapState('appointment', [
                'clinic',
                'date',
                'place',
                'placeId',
                'sort',
            ]),

            ...mapState('appointments', [
                'appointments',
                'loadingAppointments',
            ]),

            ...mapState('google', [
                'googleLoaded',
            ]),
        },

        watch: {
            date(value) {
                if (value && !value.isSame(this.dateInput)) {
                    this.dateInput = value.toDate();
                    this.$forceUpdate();
                }
            },

            sortInput(value) {
                this.setSort(value);
            },
        },

        mounted() {
            const inputDate = this.date || FormCriteria.getDate();

            this.locationInput = this.place ? this.place.formatted_address : (this.queryString.location || '');
            this.dateInput = inputDate ? inputDate.toDate() : '';
            this.sortInput = this.sort || FormCriteria.getSort();
            this.hasBeenSubmitted = this.hasBeenSubmitted || this.appointments.length;

            this.setDate(inputDate);
            this.setSort(this.sortInput);

            this.listen('google.load', this.initPlacesAutocomplete);
            this.listen('google.place.load', this.updatePlaceName);
            this.listen('appointments.reload', this.resubmitForm);
            this.listen('geolocation.allowed', this.geolocationPlaceLoaded);
            this.listen('geolocation.blocked', this.geolocationBlocked);
            this.listen('geolocation.failed', this.geolocationFailed);
            this.listen('search.submit', this.submit);
            this.listen('location.blur', this.blurLocationInput);

            this.initPlacesAutocomplete();
        },

        destroyed() {
            this.silence('google.load', this.initPlacesAutocomplete);
            this.silence('google.place.load', this.updatePlaceName);
            this.silence('appointments.reload', this.resubmitForm);
            this.silence('geolocation.allowed', this.geolocationPlaceLoaded);
            this.silence('geolocation.blocked', this.geolocationBlocked);
            this.silence('geolocation.failed', this.geolocationFailed);
            this.silence('search.submit', this.submit);
            this.silence('location.blur', this.blurLocationInput);
        },

        methods: {
            ...mapActions('appointment', [
                'setClinicId',
                'setDate',
                'setPlace',
                'setPlaceId',
                'setSort',
            ]),

            ...mapActions('appointments', [
                'getAppointments',
            ]),

            ...mapActions('footer', ['setFooterMessage']),

            /**
             * Initialise the places autocomplete.
             *
             * Note: triggered when this module is mounted, or the global 'google.load' event is dispatched.
             */
            initPlacesAutocomplete() {
                if (this.googleLoaded === false) {
                    return;
                }

                let options = {
                    types: ['geocode'],
                    fields: ['address_component', 'geometry', 'formatted_address', 'place_id'],
                    componentRestrictions: { country: 'uk' },
                };

                this.autocomplete = new window.google.maps.places.Autocomplete(
                    this.$refs.location, options
                );

                this.autocomplete.addListener('place_changed', this.dispatchPlaceChangedEvent);

                // If we've been given a part of a location without a place ID, focus
                // the location input field to prompt the user to keep typing.
                if (this.locationInput && !this.queryString.place_id) {
                    setTimeout(() => this.$refs.location.focus(), 100);
                }
            },

            /**
             * Set the location's input value.
             *
             * Note: triggered when the global 'google.place.load' event is dispatched.
             */
            updatePlaceName(place) {
                this.locationInput = place.formatted_address;

                // If the place change is a result of loading a clinic, submit the form automatically.
                if (this.clinic || this.autoSubmit) {
                    this.submit();
                }
            },

            /**
             * Dispatch an event with the newly selected place.
             *
             * Note: we listen for this event in the mounted() method of this component.
             */
            dispatchPlaceChangedEvent() {
                this.locationError = null;

                const place = this.autocomplete.getPlace();

                this.dispatch('google.place.load', place);

                this.resubmitForm();
            },

            blurLocationInput() {
                this.$refs.location.blur();
            },

            /**
             * Attempt to retrieve the user's location.
             */
            initBrowserGeolocation() {
                this.$getLocation({ enableHighAccuracy: false })
                    .then(coordinates => Place.geocodeCoordinates(coordinates))
                    .catch(() => this.dispatch('geolocation.blocked'));
            },

            /**
             * The user gave permission to use their location and Google found a place.
             */
            geolocationPlaceLoaded(place) {
                this.locationError = null;

                this.dispatch('google.place.load', place);

                this.resubmitForm();
            },

            /**
             * The user said no to using their location.
             */
            geolocationBlocked() {
                this.$refs.currentLocation.parentNode.removeChild(
                    this.$refs.currentLocation
                );

                if (!this.locationInput) {
                    this.locationError = 'Please enter your location manually';
                    this.$refs.location.select();
                }
            },

            /**
             * Something went wrong with geolocation.
             */
            geolocationFailed() {
                this.$refs.currentLocation.parentNode.removeChild(
                    this.$refs.currentLocation
                );

                if (!this.locationInput) {
                    this.locationError = 'Please enter your location manually';
                    this.$refs.location.select();
                }
            },

            /**
             * Set the selected date.
             *
             * Note: this is the callback for the datepicker.
             */
            setSelectedDate(dateTimeString) {
                const dateTime = window.moment(dateTimeString);

                this.setDate(dateTime);

                this.dateInput = dateTime.toDate();

                this.resubmitForm();
            },

            /**
             * Clear the selected clinic.
             */
            clearSelectedClinic() {
                this.setClinicId(null);

                this.locationInput = this.place.formatted_address;

                this.$nextTick(() => this.resubmitForm());
            },

            /**
             * Handle the form submission.
             */
            submit(e) {
                e && typeof e.preventDefault === 'function' && e.preventDefault();

                if (this.isSubmitting) {
                    return;
                }

                this.locationError = null;

                if (!this.place) {
                    this.$refs.location.select();
                    return;
                }

                this.isSubmitting = true;
                this.hasBeenSubmitted = true;

                const callback = () => this.$nextTick(() => this.$scrollTo('#appointments'));

                const params = {};

                if (this.clinicLimit !== null) {
                  params.clinicLimit = this.clinicLimit;
                }

                this.getAppointments({ callback, params });

                this.isSubmitting = false;
            },

            resubmitForm(e) {
                if (this.hasBeenSubmitted || e) {
                    if (this.isSubmitting) {
                        return;
                    }

                    this.isSubmitting = true;
                    this.hasBeenSubmitted = true;

                    const callback = () => this.$nextTick(() => this.$scrollTo('#appointments'));

                    let params = {};

                    if (this.clinicLimit !== null) {
                      params.clinicLimit = this.clinicLimit;
                    }

                    if (e) {
                        params.startDate = e.date;
                    }

                    this.getAppointments({ callback, params });

                    this.isSubmitting = false;
                }
            },
        },
    }
</script>

<style lang="scss">
    .search-container {
      background: rgba(20, 94, 128, 0.05);
    }

    .search-inner form {
        @apply px-4;

        @screen sm {
            max-width: 60%;
        }

        @screen md {
            max-width: 100%;
        }

        @screen lg {
            max-width: 1000px;
        }

        @screen xl {
            max-width: 1100px;
        }

        .form-group {
            @apply mb-2;
        }

        .btn {
            @apply px-1 text-center;
        }
    }
</style>
